import { useCallback, useState } from 'react';
import type { TFunction } from 'i18next';
import { describeError } from '../errors.js';
import type { ToastMessage } from '../types.js';

interface AsyncActionOptions {
  successKey?: string;
  throwOnError?: boolean;
  afterSuccess?: () => Promise<void> | void;
}

export function useAsyncAction(
  onToast: (toast: ToastMessage) => void,
  t: TFunction
) {
  const [busy, setBusy] = useState(false);

  const run = useCallback(
    async <T>(
      action: () => Promise<T>,
      options: AsyncActionOptions = {}
    ): Promise<T | undefined> => {
      setBusy(true);
      try {
        const result = await action();
        await options.afterSuccess?.();
        if (options.successKey) {
          onToast({ tone: 'success', message: t(options.successKey) });
        }
        return result;
      } catch (error) {
        onToast({ tone: 'error', message: describeError(error, t) });
        if (options.throwOnError) throw error;
        return undefined;
      } finally {
        setBusy(false);
      }
    },
    [onToast, t]
  );

  return { busy, run };
}
